import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../../../../components/ui/common/badge";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent } from "../../../../../components/ui/common/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/ui/common/tooltip";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON } from "../../../../../config/constants";
import ProductMatching from "../../../../product/component/productMatching/ProductMatching";
import { getSalesUnitImage } from "../../../shared/salesUnitImageStorage";
import { ProductDetailsViewModel } from "../../types/productDetailsView.types";
import { PRODUCT_DETAILS_TABLE_ROW_CLASS } from "../shared/tableStyles";

interface SummaryTabProps {
  vm: ProductDetailsViewModel;
}

const CARD_HEADER_CLASS = "tw-text-sm tw-font-semibold tw-text-primary";

const Field = ({ label, value, mono }: { label: string; value?: string; mono?: boolean }) => (
  <div>
    <p className="tw-mb-1 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
      {label}
    </p>
    <p
      className={`tw-text-sm ${value ? "tw-text-foreground" : "tw-text-muted-foreground"} ${
        mono ? "tw-font-mono" : ""
      }`}
    >
      {value || "-"}
    </p>
  </div>
);

/**
 * "Product Summary" tab — matches the Lovable prototype's `ProductSummaryTab`
 * exactly: compact read-only `Field`s (label + plain text, no input boxes)
 * rather than disabled `Input`/`Textarea` controls, a `Badge` for Average
 * Impact Product (primary-tinted only when true — not a two-button toggle,
 * which made "No" look like an oddly-highlighted active state), and the
 * `text-sm font-semibold text-primary` card-header convention this detail
 * view uses (as opposed to the small-caps eyebrow style in the register/edit
 * drawers). `ProductMatching` now only renders for products that actually
 * are Average Impact products — it was rendering unconditionally before,
 * showing an empty "select a sub-company" panel on ordinary products.
 */
const SummaryTab = ({ vm }: SummaryTabProps) => {
  const navigate = useNavigate();
  const productDetails = vm.productDetails;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <Card className="tw-gap-0 tw-py-0 tw-rounded-lg">
        <div className="tw-border-b tw-border-primary/20 tw-px-5 tw-py-3">
          <p className={CARD_HEADER_CLASS}>Product Info</p>
        </div>
        <CardContent className="tw-grid tw-grid-cols-1 tw-gap-5 tw-p-5 sm:tw-grid-cols-2">
          <Field label="Name" value={productDetails?.product_name} />
          <Field label="Register Number" value={productDetails?.registration_number} mono />
          <Field label="Product Type" value={productDetails?.product_type} mono />

          {!DISABLE_IS_ROOT_PRODUCT_BUTTON ? (
            <div>
              <div className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5">
                <p className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                  Average Impact Product
                </p>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="tw-h-3 tw-w-3 tw-text-muted-foreground/60" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="tw-max-w-[260px] tw-text-xs tw-leading-relaxed">
                      Marks this product as an average representing a group of similar products, rather
                      than one specific recipe.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Badge
                variant="outline"
                className={vm.productIsAverage ? "tw-border-primary/30 tw-bg-primary/10 tw-text-primary" : ""}
              >
                {vm.productIsAverage ? "Yes" : "No"}
              </Badge>
            </div>
          ) : null}

          <div className="sm:tw-col-span-2">
            <Field label="Additional Information" value={productDetails?.product_other_info} />
          </div>

          {productDetails?.product_image_path ? (
            <div className="sm:tw-col-span-2">
              <p className="tw-mb-1 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Product Image
              </p>
              <img
                src={productDetails.product_image_path}
                alt="Product"
                className="tw-h-[150px] tw-w-auto tw-rounded-md tw-border tw-border-border tw-object-contain"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="tw-gap-0 tw-overflow-hidden tw-rounded-lg tw-py-0">
        <div className="tw-border-b tw-border-primary/20 tw-px-5 tw-py-3">
          <p className={CARD_HEADER_CLASS}>Sales Units</p>
        </div>
        <div className="tw-overflow-x-auto tw-bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product Name</TableHead>
                <TableHead className="tw-text-right">Unit Size</TableHead>
                <TableHead>Unit Type</TableHead>
                <TableHead>EAN Barcode</TableHead>
                <TableHead className="tw-text-right">kgCO2eq/kg</TableHead>
                <TableHead>Image</TableHead>
                {!vm.productIsAverage ? <TableHead className="tw-text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {vm.filteredSalesUnit.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={vm.productIsAverage ? 6 : 7}
                    className="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground"
                  >
                    No sales units registered.
                  </TableCell>
                </TableRow>
              ) : (
                vm.filteredSalesUnit.map((unit: any, index: number) => (
                  <TableRow key={unit?._id ?? index} className={PRODUCT_DETAILS_TABLE_ROW_CLASS}>
                    <TableCell className="tw-font-medium">{unit?.sales_unit_name || "-"}</TableCell>
                    <TableCell className="tw-text-right tw-font-mono">
                      {unit?.sales_unit_size ?? "-"}
                    </TableCell>
                    <TableCell className="tw-font-mono">{unit?.sales_unit_type || "-"}</TableCell>
                    <TableCell className="tw-font-mono tw-text-muted-foreground">
                      {unit?.ean_barcode || "-"}
                    </TableCell>
                    <TableCell className="tw-text-right tw-font-mono">
                      {unit?.climate_value != null ? Number(unit.climate_value).toFixed(2) : "-"}
                    </TableCell>
                    <TableCell>
                      {unit?._id && getSalesUnitImage(unit._id) ? (
                        <img
                          src={getSalesUnitImage(unit._id) as string}
                          alt={unit?.sales_unit_name || "Unit"}
                          className="tw-h-10 tw-w-10 tw-rounded tw-border tw-border-border tw-object-cover"
                        />
                      ) : (
                        <span className="tw-text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {!vm.productIsAverage ? (
                      <TableCell className="tw-text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/mapping/product?sourceId=${unit?._id}`)}
                        >
                          View Mapping
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {!DISABLE_IS_ROOT_PRODUCT_BUTTON && vm.productIsAverage ? (
        <ProductMatching id={productDetails?._id} matchingContentView={false} disableEdit />
      ) : null}
    </div>
  );
};

export default SummaryTab;
